/**
 * Asset list table with type badges and action buttons.
 */

import DataTable, { type Column } from '@/components/common/DataTable';
import { formatDate } from '@/utils/formatters';
import { Key, Trash2 } from 'lucide-react';
import type { Asset } from '@/types/asset';
import {coreApiMode} from '@/config/deployment';

interface AssetListProps {
  assets: Asset[];
  onDelete?: (asset: Asset) => void;
  onRowClick?: (asset: Asset) => void;
  canManage: boolean;
}

/** Map asset type to a styled badge. */
function typeBadge(type: string) {
  const colors: Record<string, string> = {
    domain: 'bg-purple-100 text-purple-700',
    ipv4: 'bg-blue-100 text-blue-700',
    ipv6: 'bg-cyan-100 text-cyan-700',
    ip_range: 'bg-green-100 text-green-700',
    url: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[type] ?? 'bg-gray-100 text-gray-600'}`}>
      {type.replace('_', ' ')}
    </span>
  );
}

export default function AssetList({ assets, onDelete, onRowClick, canManage }: AssetListProps) {
  const columns: Column<Asset>[] = [
    { key: 'label', header: 'Label', sortable: true },
    {
      key: 'asset_type',
      header: 'Type',
      render: (item: Asset) => typeBadge(item.asset_type),
    },
    { key: 'value', header: 'Value', sortable: true },
    {
      key: 'is_active',
      header: 'Status',
      render: (item: Asset) => (
        <span className={item.is_active ? 'text-green-600' : 'text-gray-400'}>
          {item.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'credentials',
      header: '',
      className: 'w-8',
      render: (item: Asset) =>
        item.credentials && item.credentials.length > 0 ? (
          <span title={`${item.credentials.length} credential(s) attached`}>
            <Key className="h-3.5 w-3.5 text-blue-500" />
          </span>
        ) : null,
    },
    {
      key: 'created_at',
      header: 'Added',
      sortable: true,
      render: (item: Asset) => formatDate(item.created_at),
    },
  ];
  if(coreApiMode)columns.splice(4,0,{key:'verification_status',header:'Ownership',render:(item:Asset)=><span className={item.verification_status==='verified'?'text-green-700':'text-amber-700'}>{item.is_internal?'Internal · agent':item.verification_status==='verified'?item.verified_domain?`Verified via ${item.verified_domain}`:item.verification_scope==='asset'&&item.registrable_domain?'Host-only verified':'Verified':'Verification required'}</span>});

  // Add actions column if user can manage assets.
  if (canManage && onDelete) {
    columns.push({
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (item: Asset) => (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          title="Delete asset"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    });
  }

  return (
    <DataTable
      columns={columns}
      data={assets}
      rowKey={(item) => item.id}
      onRowClick={onRowClick}
      emptyMessage="No assets configured. Add domains, IPs, or URLs to get started."
    />
  );
}
